import { useCallback, useRef, useState } from "react";
import { useAdminWaitlist, useDeleteWaitlistEntry } from "../../../lib/api/admin.api";

interface PendingDelete {
  id: string;
  email: string;
}

export function AdminWaitlistTab() {
  const { data: entries, isLoading } = useAdminWaitlist();
  const deleteEntry = useDeleteWaitlistEntry();
  const [copied, setCopied] = useState(false);
  const [pending, setPending] = useState<PendingDelete | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDelete = useCallback((id: string, email: string) => {
    // Cancel previous pending delete
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      if (pending) deleteEntry.mutate(pending.id);
    }

    setHidden((prev) => new Set(prev).add(id));
    setPending({ id, email });

    timeoutRef.current = setTimeout(() => {
      deleteEntry.mutate(id);
      setPending(null);
      setHidden((prev) => { const next = new Set(prev); next.delete(id); return next; });
      timeoutRef.current = null;
    }, 4000);
  }, [pending, deleteEntry]);

  const handleUndo = useCallback(() => {
    if (!pending) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setHidden((prev) => { const next = new Set(prev); next.delete(pending.id); return next; });
    setPending(null);
    timeoutRef.current = null;
  }, [pending]);

  if (isLoading) return <p className="text-ink-muted text-sm">Chargement...</p>;
  if (!entries?.length) return <p className="text-ink-muted text-sm">Aucune inscription</p>;

  const visibleEntries = entries.filter((e) => !hidden.has(e.id));

  const handleCopyAll = async () => {
    const emails = visibleEntries.map((e) => e.email).join("\n");
    await navigator.clipboard.writeText(emails);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-ink">
          {visibleEntries.length} inscrit{visibleEntries.length > 1 ? "s" : ""}
        </p>
        <button
          type="button"
          onClick={handleCopyAll}
          className="px-3 py-2 text-xs font-medium bg-surface-alt border border-border/60 rounded-md
            hover:bg-border text-ink-muted hover:text-ink transition-colors"
        >
          {copied ? "Copié !" : "Copier tous les emails"}
        </button>
      </div>

      {pending && (
        <div className="flex items-center justify-between p-3 bg-danger/10 border border-danger/20 rounded-xl">
          <span className="text-sm text-ink-muted">
            <span className="font-medium text-ink">{pending.email}</span> supprimé
          </span>
          <button
            type="button"
            onClick={handleUndo}
            className="px-3 py-1 text-xs font-semibold text-accent hover:text-accent-hover transition-colors"
          >
            Annuler
          </button>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        {visibleEntries.map((entry) => (
          <div
            key={entry.id}
            className="flex items-center justify-between p-3 bg-surface-alt rounded-xl"
          >
            <span className="text-sm text-ink font-medium truncate">
              {entry.email}
            </span>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              <span className="text-[10px] text-ink-muted">
                {new Date(entry.createdAt).toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
              <button
                type="button"
                onClick={() => handleDelete(entry.id, entry.email)}
                className="p-1 text-ink-faint hover:text-danger transition-colors"
                title="Supprimer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
